import { GdmtMedicationClass, MedicationRequestStatus, TitrationDrugClass } from "@/utils/GraphQLClient/titration";

import { fhirR4 } from "@smile-cdr/fhirts";

export const TITRATION_FAMILY_SNOMED_CODE = "736378000";

export enum VitallExtensionUrl {
  MedicationTargetDose = "https://vitall.com/fhir/StructureDefinition/medicationTargetDose",
  MedicationPreviousDose = "https://vitall.com/fhir/StructureDefinition/medicationPreviousDose",
  TitrationProgram = "https://vitall.com/fhir/StructureDefinition/titrationProgram",
  TitrationDrugClass = "https://vitall.com/fhir/StructureDefinition/titrationDrugClass",
  TitrationDecision = "https://vitall.com/fhir/StructureDefinition/titrationDecision",
  TitrationDecisionReason = "https://vitall.com/fhir/StructureDefinition/titrationDecisionReason",
  TitrationEndReason = "https://vitall.com/fhir/StructureDefinition/titrationEndReason",
}

export enum VitallCodeSystem {
  TitrationProgram = "https://vitall.com/fhir/CodeSystem/titrationProgram",
  TitrationDrugClass = "https://vitall.com/fhir/CodeSystem/titrationDrugClass",
  TitrationDecision = "https://vitall.com/fhir/CodeSystem/titrationDecision",
  TitrationDecisionReason = "https://vitall.com/fhir/CodeSystem/titrationDecisionReason",
  TitrationEndReason = "https://vitall.com/fhir/CodeSystem/titrationEndReason",
}

const TITRATION_DECISION_EXT_URL = "https://vitall.com/fhir/StructureDefinition/titrationDecision";
const TITRATION_DECISION_REASON_EXT_URL = "https://vitall.com/fhir/StructureDefinition/titrationDecisionReason";

export { TITRATION_DECISION_EXT_URL, TITRATION_DECISION_REASON_EXT_URL };

export type TitrationMedByClass = Record<GdmtMedicationClass, fhirR4.MedicationStatement[]>;

export enum TitrationDecision {
  Increase = "Increase",
  Decrease = "Decrease",
  Hold = "Hold",
  Continue = "Continue",
  Discontinue = "Discontinue",
}

export enum HoldOrContinueTitrationDecisionReason {
  Symptomatic = "Symptomatic",
  Away = "Away",
  AbnormalBloodwork = "AbnormalBloodwork",
  Admitted = "Admitted",
  DecliningFurther = "DecliningFurther",
  Other = "Other",
}

export enum DiscontinueTitrationDecisionReason {
  Symptomatic = "Symptomatic",
  AbnormalBloodwork = "AbnormalBloodwork",
  DecliningFurther = "DecliningFurther",
  AllergicReaction = "AllergicReaction",
  AdverseReaction = "AdverseReaction",
  Other = "Other",
}

export const DRUG_CLASS_FHIR_CODE_TO_ENUM = {
  betaBlockers: TitrationDrugClass.BetaBlocker,
  mras: TitrationDrugClass.nsMRA,
  rasi: TitrationDrugClass.RASi,
  sglt2i: TitrationDrugClass.SGLT2i,
};

/**
 * Purpose: 'on-hold' becomes 'onHold' due to standard GraphQL enum serialization
 */
export const MED_REQUEST_STATUS_ENUM_TO_FHIR_STATUS = {
  [MedicationRequestStatus.active]: "active",
  [MedicationRequestStatus.cancelled]: "cancelled",
  [MedicationRequestStatus.completed]: "completed",
  [MedicationRequestStatus.draft]: "draft",
  [MedicationRequestStatus.enteredInError]: "entered-in-error",
  [MedicationRequestStatus.onHold]: "on-hold",
  [MedicationRequestStatus.stopped]: "stopped",
  [MedicationRequestStatus.unknown]: "unknown",
} satisfies Record<MedicationRequestStatus, string>;

/** Every valid decision reason — the union of both decision-specific reason sets. */
export type TitrationDecisionReason =
  keyof typeof DiscontinueTitrationDecisionReason | keyof typeof HoldOrContinueTitrationDecisionReason;

const TITRATION_DECISION_REASON_VALUES = new Set<string>([
  ...Object.values(DiscontinueTitrationDecisionReason),
  ...Object.values(HoldOrContinueTitrationDecisionReason),
]);

/** Narrows an arbitrary string (e.g. a FHIR coding `code`) to a `TitrationDecisionReason`. */
export const isTitrationDecisionReason = (value: string): value is TitrationDecisionReason =>
  TITRATION_DECISION_REASON_VALUES.has(value);

/**
 * Resolve a titration decision from a coding `code`, tolerating both encodings:
 * the raw FHIR CodeSystem code (lowercase, e.g. `"discontinue"`) returned over REST,
 * and the GraphQL-serialized enum value (e.g. `"Discontinue"`). Returns `undefined`
 * for an unrecognised code.
 */
export const toTitrationDecision = (code: string | undefined): TitrationDecision | undefined =>
  code ? Object.values(TitrationDecision).find(value => value.toLowerCase() === code.toLowerCase()) : undefined;

/**
 * Resolve a titration decision reason from a coding `code`, tolerating both the raw
 * FHIR kebab-case code (e.g. `"allergic-reaction"`) returned over REST and the
 * GraphQL-serialized enum value (e.g. `"AllergicReaction"`). Returns `undefined`
 * for an unrecognised code.
 */
export const toTitrationDecisionReason = (code: string | undefined): TitrationDecisionReason | undefined => {
  if (!code) {
    return undefined;
  }
  const normalized = code.replace(/-/g, "").toLowerCase();
  return [...TITRATION_DECISION_REASON_VALUES].find(value => value.toLowerCase() === normalized) as
    TitrationDecisionReason | undefined;
};
